﻿import React, { useState } from "react";
import { Dropdown } from "react-bootstrap";
import {
  Menu,
  ChevronDown,
  LayoutGrid,
  List as ListIcon,
  Copy,
  Plus,
} from "lucide-react";
import WatchlistCreate from "./WatchlistCreate.jsx";
import WatchlistTable from "./WatchlistTable.jsx";
import AddCompaniesModal from "./AddCompaniesModal.jsx";
import SideCards from "./SideCards.jsx";
import Sparkline from "./Sparkline.jsx";
import { getCompany, useCompanyInfoVersion } from "../utils/companyInfo.js";
import { getStockStats } from "../utils/priceData.js";
import { loadWatchlists, saveWatchlists } from "../utils/watchlists.js";

export default function Watchlist({ user, onBack }) {
  useCompanyInfoVersion();
  const [data, setData] = useState(() => loadWatchlists(user));
  const [view, setView] = useState(data.lists.length ? "list" : "create");
  const [returnView, setReturnView] = useState("list");
  const [showAdd, setShowAdd] = useState(false);

  const active = data.lists.find((l) => l.id === data.activeId) || data.lists[0];

  const commit = (next) => {
    setData(next);
    saveWatchlists(user, next);
  };

  const openCreate = () => {
    setReturnView(view === "create" ? "list" : view);
    setView("create");
  };

  const handleCreate = (name) => {
    const id = Date.now().toString(36);
    commit({ lists: [...data.lists, { id, name, tickers: [] }], activeId: id });
    setView("table"); // after creating: table view
  };

  const goBack = () => {
    if (data.lists.length) setView(returnView);
    else onBack();
  };

  const selectList = (id) => commit({ ...data, activeId: id });

  const toggleTicker = (ticker) => {
    const lists = data.lists.map((l) =>
      l.id !== active.id
        ? l
        : {
            ...l,
            tickers: l.tickers.includes(ticker)
              ? l.tickers.filter((t) => t !== ticker)
              : [...l.tickers, ticker],
          }
    );
    commit({ ...data, lists });
  };

  /* ---------- BEFORE: create a watchlist ---------- */
  if (view === "create" || !active) {
    return (
      <WatchlistCreate
        existingNames={data.lists.map((l) => l.name)}
        onSave={handleCreate}
        onBack={goBack}
      />
    );
  }

  const titleDropdown = (
    <Dropdown>
      <Dropdown.Toggle variant="" className="wl-title-toggle">
        <Menu size={18} /> <span>{active.name}</span> <ChevronDown size={16} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {data.lists.map((l) => (
          <Dropdown.Item key={l.id} active={l.id === active.id} onClick={() => selectList(l.id)}>
            {l.name}
          </Dropdown.Item>
        ))}
        <Dropdown.Divider />
        <Dropdown.Item onClick={openCreate}>
          <Plus size={14} className="me-1" /> Create new watchlist
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  const addModal = showAdd && (
    <AddCompaniesModal
      selected={active.tickers}
      onToggle={toggleTicker}
      onClose={() => setShowAdd(false)}
    />
  );

  /* ---------- AFTER: table view ---------- */
  if (view === "table") {
    return (
      <main className="page-bg">
        <div className="dash-main-content wl-wide">
          <nav className="wl-breadcrumb">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setView("list");
              }}
            >
              {active.name}
            </a>
            <span>/</span>
            <span>Table View</span>
          </nav>

          <div className="top-bar">
            {titleDropdown}
            <div className="d-flex gap-2">
              <button className="btn tool-btn" onClick={() => setShowAdd(true)}>
                <Plus size={15} /> ADD COMPANIES
              </button>
              <button className="btn tool-btn" onClick={() => setView("list")}>
                <ListIcon size={15} /> COMPANIES
              </button>
            </div>
          </div>

          <WatchlistTable
            list={active}
            onAdd={() => setShowAdd(true)}
            onRemove={toggleTicker}
          />
        </div>
        {addModal}
      </main>
    );
  }

  /* ---------- Watchlist home (company cards) ---------- */
  const companies = active.tickers
    .map((t) => getCompany(t))
    .filter(Boolean);

  return (
    <main className="page-bg">
      <div className="dash-main-content">
        <div className="top-bar">
          {titleDropdown}

          <div className="view-toggle">
            <button className="toggle-btn active">
              <LayoutGrid size={14} /> WATCHLIST VIEW
            </button>
            <button className="toggle-btn" onClick={() => setView("table")} title="Table view">
              <ListIcon size={14} />
            </button>
          </div>
        </div>

        <div className="dash-grid">
          {companies.length === 0 ? (
            <div className="card empty-card">
              <div className="empty-icon">
                <Copy size={36} />
              </div>
              <h5 className="card-title fw-bold">Add companies to watchlist</h5>
              <p className="card-text text-muted">
                Keep track of latest announcements, insider trades and credit ratings of your companies.
              </p>
              <button className="btn btn-purple" onClick={() => setShowAdd(true)}>
                ADD COMPANIES
              </button>
            </div>
          ) : (
            <div>
              <div className="row g-3">
                {companies.map((c) => {
                  const stats = getStockStats(c.ticker);
                  const up = stats.change >= 0;
                  return (
                    <div key={c.ticker} className="col-12 col-md-6">
                      <div className="card wl-company-card">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fw-bold">{c.name}</div>
                            <div className="text-muted small">{c.ticker}</div>
                          </div>
                          <span className="badge badge-purple">{c.category}</span>
                        </div>
                        <div className="d-flex justify-content-between align-items-end mt-3">
                          <div>
                            <div className="wl-price">${stats.current.toFixed(2)}</div>
                            <div className={`small fw-bold ${up ? "change-up" : "change-down"}`}>
                              {up ? "+" : ""}
                              {stats.change.toFixed(1)}% (52W)
                            </div>
                          </div>
                          <Sparkline data={stats.series} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button className="show-more-btn mt-3" onClick={() => setShowAdd(true)}>
                ADD COMPANIES
              </button>
              <p className="text-muted small mt-3 mb-0">
                Prices come from your database and are about 15 minutes delayed.
              </p>
            </div>
          )}

          <SideCards />
        </div>
      </div>
      {addModal}
    </main>
  );
}



